(() => {
  const stickyCart = document.querySelector("[data-sticky-cart]");
  if (!stickyCart || stickyCart.dataset.initialized === "true") return;

  stickyCart.dataset.initialized = "true";

  const trigger = stickyCart.querySelector("[data-sticky-cart-trigger]");
  const countBadge = stickyCart.querySelector("[data-sticky-cart-count]");
  const cartPagePath = new URL(window.routes?.cart_url || "/cart", window.location.origin).pathname.replace(/\/$/, "");
  let currentCount = Number.parseInt(stickyCart.dataset.stickyCartValue, 10) || 0;
  let syncTimer;
  let headerCartObserver;
  let observedHeaderCart;
  let drawerObserver;
  let observedDrawer;
  let syncRequestId = 0;

  const normalizeCount = (value) => {
    const count = Number.parseInt(value, 10);
    return Number.isFinite(count) && count >= 0 ? count : null;
  };

  const setCount = (value) => {
    const count = normalizeCount(value);
    if (count === null) return;
    if (count === currentCount && countBadge.textContent.trim() === String(count)) return;

    currentCount = count;
    stickyCart.dataset.stickyCartValue = String(count);
    countBadge.textContent = String(count);
  };

  const countFromEvent = (event) => {
    const detail = event?.detail || event || {};
    const candidates = [
      detail.cartData?.item_count,
      detail.parsedState?.item_count,
      detail.sectionsData?.item_count,
      detail.cart?.item_count,
      detail.item_count,
      detail.newTotalCount,
    ];

    for (const candidate of candidates) {
      const count = normalizeCount(candidate);
      if (count !== null) return count;
    }

    return null;
  };

  const cartJsonUrl = () => {
    const cartUrl = window.routes?.cart_url || "/cart";
    return `${cartUrl.replace(/\.js(?:\?.*)?$/, "")}.js`;
  };

  const syncFromCart = async () => {
    const requestId = ++syncRequestId;
    try {
      const response = await fetch(cartJsonUrl(), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!response.ok) return null;

      const cart = await response.json();
      if (requestId !== syncRequestId) return null;
      setCount(cart.item_count);
      return cart;
    } catch (_error) {
      // The server-rendered count remains available if a background sync fails.
      return null;
    }
  };

  const scheduleSync = () => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncFromCart, 120);
  };

  const syncFromHeaderCart = () => {
    const headerCart = document.getElementById("cart-icon-bubble");
    if (!headerCart) return;

    const bubble = headerCart.querySelector(".cart-count-bubble");
    if (!bubble || bubble.textContent.trim() === "") {
      setCount(0);
      return;
    }

    const visibleCount = bubble.querySelector('[aria-hidden="true"]') || bubble.querySelector("span");
    const count = normalizeCount(visibleCount?.textContent);
    if (count !== null) setCount(count);
  };

  const observeHeaderCart = () => {
    const headerCart = document.getElementById("cart-icon-bubble");
    if (!headerCart || headerCart === observedHeaderCart) return;

    headerCartObserver?.disconnect();
    observedHeaderCart = headerCart;
    headerCartObserver = new MutationObserver(syncFromHeaderCart);
    headerCartObserver.observe(headerCart, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    syncFromHeaderCart();
  };

  const updateExpandedState = () => {
    trigger.setAttribute("aria-expanded", observedDrawer?.classList.contains("active") ? "true" : "false");
  };

  const observeDrawer = () => {
    const drawer = document.querySelector("cart-drawer");
    if (!drawer || drawer === observedDrawer) return drawer;

    drawerObserver?.disconnect();
    observedDrawer = drawer;
    drawerObserver = new MutationObserver(updateExpandedState);
    drawerObserver.observe(drawer, { attributes: true, attributeFilter: ["class"] });
    updateExpandedState();
    return drawer;
  };

  const openCartDrawer = async () => {
    let drawer = observeDrawer();

    if (!drawer || typeof drawer.open !== "function") {
      await customElements.whenDefined("cart-drawer");
      drawer = observeDrawer();
    }

    if (!drawer || typeof drawer.open !== "function") return;

    stickyCart.classList.add("is-opening");
    window.setTimeout(() => stickyCart.classList.remove("is-opening"), 240);
    try {
      drawer.open(trigger);
    } catch (_error) {
      // A section replacement can briefly leave focus references stale. The
      // drawer itself is still safe to open and is refreshed on the next tick.
      document.querySelectorAll(".cart__items--disabled").forEach((element) => {
        element.classList.remove("cart__items--disabled");
      });
      drawer.inert = false;
      drawer.style.transitionProperty = "none";
      drawer.style.pointerEvents = "auto";
      drawer.style.visibility = "visible";
      drawer.classList.add("animate", "active");
      drawer.setAttribute("aria-hidden", "false");
      document.body.classList.add("overflow-hidden");
      trigger.setAttribute("aria-expanded", "true");
    }
    scheduleSync();
  };

  trigger.addEventListener("click", (event) => {
    event.preventDefault();
    openCartDrawer();
  });

  // Any legacy "View cart" link should use the drawer instead of navigating
  // to Shopify's cart page.
  document.addEventListener("click", (event) => {
    if (event.defaultPrevented) return;

    const cartLink = event.target.closest("a[href]");
    if (!cartLink) return;

    let destination;
    try {
      destination = new URL(cartLink.getAttribute("href").trim(), window.location.origin);
    } catch (_error) {
      return;
    }

    const destinationPath = destination.pathname.replace(/\/$/, "");
    if (destination.origin !== window.location.origin || destinationPath !== cartPagePath) return;

    event.preventDefault();
    openCartDrawer();
  });

  [
    "cart-drawer:render",
    "cart-drawer:update",
    "cart:ajax-update",
    "cart:count-updated",
    "lytebites:bundle-cart-updated",
  ].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const count = countFromEvent(event);
      if (count === null) scheduleSync();
      else setCount(count);

      observeHeaderCart();
      observeDrawer();
    });
  });

  ["cart:update", "cart:change"].forEach((eventName) => {
    document.addEventListener(eventName, scheduleSync);
  });

  if (typeof window.subscribe === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      const count = countFromEvent(event);
      if (count === null) scheduleSync();
      else setCount(count);
    });
  }

  document.addEventListener("shopify:section:load", () => {
    observeHeaderCart();
    observeDrawer();
    scheduleSync();
  });

  window.addEventListener("pageshow", (event) => {
    if (event.persisted) scheduleSync();
  });

  observeHeaderCart();
  observeDrawer();

  try {
    if (window.sessionStorage.getItem("lytebites:open-cart-drawer") === "true") {
      window.sessionStorage.removeItem("lytebites:open-cart-drawer");
      window.setTimeout(openCartDrawer, 0);
    }
  } catch (_error) {
    // Direct cart visits still redirect home if session storage is unavailable.
  }
})();
